import { useCallback, useMemo, useState } from 'react';
import length from '@turf/length';
import { lineString } from '@turf/helpers';

export const formatDistance = (kilometers: number) => {
  if (!Number.isFinite(kilometers) || kilometers <= 0) return '0 m';

  const meters = kilometers * 1000;
  if (meters < 1000) return `${Math.round(meters)} m`;

  return `${kilometers.toFixed(2)} km`;
};

export function useMapRuler() {
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [measurePoints, setMeasurePoints] = useState<[number, number][]>([]);

  const totalDistance = useMemo(() => {
    if (measurePoints.length < 2) return 0;
    return length(lineString(measurePoints), { units: 'kilometers' });
  }, [measurePoints]);

  const toggleRuler = useCallback(() => {
    setIsMeasuring((prev) => {
      if (prev) setMeasurePoints([]);
      return !prev;
    });
  }, []);

  const addPoint = useCallback((lng: number, lat: number) => {
    setMeasurePoints((prev) => [...prev, [lng, lat]]);
  }, []);

  const removeLastPoint = useCallback(() => {
    setMeasurePoints((prev) => prev.slice(0, -1));
  }, []);

  const resetMeasure = useCallback(() => {
    setMeasurePoints([]);
  }, []);

  const clearMeasure = useCallback(() => {
    setMeasurePoints([]);
    setIsMeasuring(false);
  }, []);

  const formattedDistance = useMemo(() => formatDistance(totalDistance), [totalDistance]);

  return {
    isMeasuring,
    measurePoints,
    totalDistance,
    formattedDistance,
    toggleRuler,
    addPoint,
    removeLastPoint,
    resetMeasure,
    clearMeasure,
  };
}
